import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Accordion } from 'components/molecules/Accordion';
import { Elabels } from '../../helpers';
import Tasks from '../Tasks';
import IntelligenceReport from '../IntelligenceReport';
import Notes from '../Notes';
import ApplicationData from '../ApplicationData';
import SummaryReport from '../SummaryReport';
import {
  CaseStatus,
  FINAL_CASE_STATUSES,
  REVIEW_APPLICATION_CASE_STATUSES,
} from 'models/cases';
import { useAuth } from 'models/authContext';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { useTranslations } from 'hooks/useTranslations';
import { Loader } from 'components/atoms/Loader';
import { useCase } from '../../hooks';
import { ECaseProcessTypes } from 'constants/CaseProcessTypes';

interface IVerificationProcess {
  isUnderVerification: boolean;
  isFromLE: boolean;
  statusUniqueId: string;
  processTypeUniqueId: string;
  assignedToUniqueId?: string;
  summaryReportGenerated: boolean;
  summaryReportName: string | null;
  legalEntityId: string;
  updateCase: () => void;
}

const VerificationProcess = ({
  isUnderVerification,
  isFromLE,
  statusUniqueId,
  processTypeUniqueId,
  assignedToUniqueId,
  summaryReportGenerated,
  summaryReportName,
  legalEntityId,
  updateCase,
}: IVerificationProcess) => {
  const { caseId = '' } = useParams<{ id: string; caseId: string }>();
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const { user } = useAuth();
  const { hasPermissions } = usePermission();
  const { caseDecision, getCaseDecisionData } = useCase();
  const [isDecisionLoading, setIsDecisionLoading] = useState(false);

  const isCaseDisabled = FINAL_CASE_STATUSES.includes(statusUniqueId as CaseStatus);

  useEffect(() => {
    if (caseId && isCaseDisabled) {
      (async () => {
        setIsDecisionLoading(true);
        await getCaseDecisionData(caseId);
        setIsDecisionLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, statusUniqueId]);

  const showReviewCommets =
    REVIEW_APPLICATION_CASE_STATUSES.includes(statusUniqueId as CaseStatus) &&
    !!hasPermissions([EPermission.VERIFY_CASES]);

  const isAssignedToCurrentUser = !!assignedToUniqueId && assignedToUniqueId === user?.userId;

  const showAddTaskNote =
    statusUniqueId === CaseStatus.IN_PROGRESS &&
    !!hasPermissions([EPermission.VERIFY_CASES]) &&
    isAssignedToCurrentUser;

  const summaryReportSection = isDecisionLoading ? (
    <Loader isLoading />
  ) : (
    caseDecision && (
      <Accordion
        title={t(Elabels.summaryReport)}
        name="summaryReport"
        alwaysExpanded={isDesktop}
        expanded={isDesktop}
      >
        <SummaryReport
          caseDecision={caseDecision}
          statusUniqueId={statusUniqueId}
          legalEntityId={legalEntityId}
          summaryReportGenerated={summaryReportGenerated}
          updateCase={updateCase}
          summaryReportName={summaryReportName}
        />
      </Accordion>
    )
  );

  if (!isUnderVerification) {
    return (
      <>
        {(processTypeUniqueId === ECaseProcessTypes.APPLICATION ||
          processTypeUniqueId === ECaseProcessTypes.RENEWAL) && (
            <ApplicationData showReviewCommets={false} isFromLE={isFromLE} showWarning={false} />
          )}
        {summaryReportSection}
      </>
    );
  }
  return (
    <Grid size={{ xs: 12 }}>
      {(processTypeUniqueId === ECaseProcessTypes.APPLICATION ||
        processTypeUniqueId === ECaseProcessTypes.RENEWAL) && (
          <Accordion
            title={t(Elabels.applicationData)}
            name="applicationData"
            alwaysExpanded={isDesktop}
            expanded={isDesktop}
          >
            <ApplicationData
              showReviewCommets={showReviewCommets}
              isInProgress={statusUniqueId === CaseStatus.IN_PROGRESS}
              showWarning={REVIEW_APPLICATION_CASE_STATUSES.includes(
                statusUniqueId as CaseStatus,
              )}
              isFromLE={isFromLE}
            />
          </Accordion>
        )}
      <Accordion
        title={t(Elabels.tasks)}
        name="tasks"
        alwaysExpanded={isDesktop}
        expanded={isDesktop}
      >
        <Tasks
          showAddTaskNote={showAddTaskNote}
          isReadOnly={isCaseDisabled}
          isAssigned={isAssignedToCurrentUser}
        />
      </Accordion>
      <Accordion
        title={t(Elabels.intelligenceReport)}
        name="intelligenceReport"
        alwaysExpanded={isDesktop}
        expanded={isDesktop}
      >
        <IntelligenceReport
          isFromLE={isFromLE}
          showAdd={showAddTaskNote}
          isReadOnly={isCaseDisabled}
        />
      </Accordion>
      <Accordion
        title={t(Elabels.notes)}
        name="notes"
        alwaysExpanded={isDesktop}
        expanded={isDesktop}
      >
        <Notes
          isFromLE={isFromLE}
          showAddTaskNote={showAddTaskNote}
          isReadOnly={isCaseDisabled || !isAssignedToCurrentUser}
        />
      </Accordion>
      {summaryReportSection}
    </Grid>
  );
};

export default VerificationProcess;
